import { DeleteOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { UseFilterObject } from 'modules/common/hooks/useFilter';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FilterChip from './FilterChip';
import { MoreFiltersPopover } from './MoreFiltersPopover';
import styles from './styles.module.scss';
import { ChipData, FiltersConfig } from './types';

import Button from '../Button';

type FilterChipsProps<T> = {
  filterHook: UseFilterObject<T>;
  config: FiltersConfig<T>;
  // Fields that should NOT be shown in chips (e.g. search, hidden filters)
  excludeFields?: (keyof T)[];
  maxVisible?: number;
  onClearAll?: () => void;
};

export const FilterChips = <T extends Record<string, any>>({
  filterHook,
  config,
  excludeFields = [],
  maxVisible = 4,
  onClearAll,
}: FilterChipsProps<T>) => {
  const { t } = useTranslation('common.FilterChips');

  const { options, setFilterOption, removeFilterOption, clearFilters } =
    filterHook;

  const chips = useMemo(() => {
    const result: ChipData[] = [];

    options.forEach(({ fieldName, value }) => {
      if (excludeFields.includes(fieldName as keyof T)) {
        return;
      }

      if (
        value === undefined ||
        value === null ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return;
      }

      const fieldConfig = config[fieldName as keyof T];
      const label = fieldConfig?.label || String(fieldName);

      const valuesArray = Array.isArray(value) ? value : [value];

      valuesArray.forEach((singleValue, index) => {
        let displayValue = String(singleValue);

        if (fieldConfig?.renderValue) {
          displayValue = fieldConfig.renderValue(singleValue);
        }

        result.push({
          id: `${String(fieldName)}-${index}-${JSON.stringify(singleValue)}`,
          fieldName: String(fieldName),
          value: singleValue,
          label,
          displayValue,
        });
      });
    });

    return result;
  }, [options, config, excludeFields]);

  const handleRemoveChip = (chip: ChipData) => {
    const currentOption = options.find((o) => o.fieldName === chip.fieldName);
    if (!currentOption) {
      return;
    }

    if (Array.isArray(currentOption.value)) {
      const newValue = currentOption.value.filter((v: any) => {
        if (typeof v !== 'object') {
          return v !== chip.value;
        }

        return JSON.stringify(v) !== JSON.stringify(chip.value);
      });

      if (newValue.length === 0) {
        removeFilterOption(chip.fieldName as keyof T);
      } else {
        setFilterOption({
          fieldName: chip.fieldName as keyof T,
          value: newValue,
        });
      }
    } else {
      removeFilterOption(chip.fieldName as keyof T);
    }
  };

  const handleClearAll = () => {
    clearFilters(excludeFields);
    onClearAll?.();
  };

  if (chips.length === 0) {
    return null;
  }

  const visibleChips = chips.slice(0, maxVisible);
  const overflowChips = chips.slice(maxVisible);

  return (
    <div className={styles.filterChipsContainer}>
      <span className={styles.label}>{t('activeFilters')}</span>

      <div className={styles.chipsWrapper}>
        {visibleChips.map((chip) => (
          <FilterChip
            key={chip.id}
            fieldLabel={chip.label}
            displayValue={chip.displayValue}
            onRemove={() => handleRemoveChip(chip)}
          />
        ))}

        {overflowChips.length > 0 && (
          <MoreFiltersPopover
            chips={overflowChips}
            onRemove={handleRemoveChip}
          />
        )}
      </div>

      <Tooltip title={t('resetAllFilters')}>
        <Button
          type="link"
          className={styles.clearAllButton}
          icon={<DeleteOutlined />}
          onClick={handleClearAll}
        >
          {t('clearAllFilters')}
        </Button>
      </Tooltip>
    </div>
  );
};
